import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Menu, X, User, LogOut, History, Shield, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { EASING, DURATION } from "@/lib/motionSystem";

const navLinks = [
    { to: "/", label: "Home" },
    { to: "/genz-credit-scoring", label: "Get Score" },
    { to: "/genz-credit-scoring/compare", label: "Compare" },
];

const authedNavLinks = [
    { to: "/genz-credit-scoring/history", label: "History" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const profileRef = useRef(null);

    const executiveMode = location.pathname.includes("/genz-credit-scoring/lender/")
        || location.pathname.includes("/genz-credit-scoring/admin");

    // Bug 3: close profile dropdown on outside click + on route change
    useEffect(() => {
        if (!profileOpen) return undefined;
        const onClickOutside = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target)) {
                setProfileOpen(false);
            }
        };
        const onEscape = (e) => { if (e.key === "Escape") setProfileOpen(false); };
        document.addEventListener("mousedown", onClickOutside);
        document.addEventListener("keydown", onEscape);
        return () => {
            document.removeEventListener("mousedown", onClickOutside);
            document.removeEventListener("keydown", onEscape);
        };
    }, [profileOpen]);

    useEffect(() => { setProfileOpen(false); }, [location.pathname]);

    const handleLogout = async () => {
        setProfileOpen(false);
        await logout();
        navigate("/");
    };

    return (
        <nav
            className={`fixed top-0 w-full z-50 backdrop-blur-2xl border-b ${
                executiveMode
                    ? "bg-white/90 border-slate-200/80"
                    : "bg-[#030712]/78 border-white/10"
            }`}
            data-testid="navbar"
        >
            <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                <Link
                    to="/"
                    className="flex items-center gap-2.5 shrink-0"
                    data-testid="nav-logo"
                >
                    <img
                        src="/gz-credit.png"
                        alt="GenZ Credit Score"
                        className="w-8 h-8 rounded-full object-cover"
                    />
                    <span className={`text-xl font-heading font-bold ${executiveMode ? "text-slate-800" : "gradient-text"}`}>
                        GenZ Credit Score
                    </span>
                </Link>

                {/* Desktop links */}
                <div className="hidden lg:flex items-center gap-8">
                    {navLinks.map(link => (
                        <Link
                            key={link.to}
                            to={link.to}
                            className={`text-sm font-medium transition-colors ${
                                location.pathname === link.to
                                    ? executiveMode ? "text-slate-900" : "text-text-primary"
                                    : executiveMode ? "text-slate-500 hover:text-slate-800" : "text-text-secondary hover:text-text-primary"
                            }`}
                            data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                        >
                            {link.label}
                        </Link>
                    ))}
                    {user && authedNavLinks.map(link => (
                        <Link
                            key={link.to}
                            to={link.to}
                            className={`text-sm font-medium transition-colors ${
                                location.pathname === link.to
                                    ? executiveMode ? "text-slate-900" : "text-text-primary"
                                    : executiveMode ? "text-slate-500 hover:text-slate-800" : "text-text-secondary hover:text-text-primary"
                            }`}
                            data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                        >
                            {link.label}
                        </Link>
                    ))}
                </div>

                {/* Desktop right side: profile dropdown or login button */}
                <div className="hidden lg:flex items-center gap-3">
                    {user ? (
                        // Bug 3: clickable profile button with dropdown menu
                        <div className="relative" ref={profileRef}>
                            <button
                                onClick={() => setProfileOpen(o => !o)}
                                className={`flex items-center gap-2 px-2 py-1.5 rounded-xl transition-all ${
                                    executiveMode
                                        ? "hover:bg-slate-100"
                                        : "hover:bg-white/5"
                                }`}
                                data-testid="nav-profile-button"
                                aria-haspopup="menu"
                                aria-expanded={profileOpen}
                            >
                                {user.avatar_url ? (
                                    <img
                                        src={user.avatar_url}
                                        alt=""
                                        className={`w-8 h-8 rounded-full object-cover ${executiveMode ? "border border-slate-200" : "border border-white/10"}`}
                                    />
                                ) : (
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${executiveMode ? "bg-slate-100 text-slate-700" : "bg-blue-primary/20 text-blue-primary"}`}>
                                        {(user.name || user.email || "U").charAt(0).toUpperCase()}
                                    </div>
                                )}
                                <span className={`text-sm font-medium max-w-[140px] truncate ${executiveMode ? "text-slate-700" : "text-text-secondary"}`}>
                                    {user.name || user.email}
                                </span>
                                <ChevronDown
                                    size={14}
                                    className={`transition-transform ${profileOpen ? "rotate-180" : ""} ${executiveMode ? "text-slate-500" : "text-text-secondary"}`}
                                />
                            </button>

                            <AnimatePresence>
                                {profileOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -8, scale: 0.96 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: -8, scale: 0.96 }}
                                        transition={{ duration: DURATION.pageTransition, ease: EASING.standard }}
                                        className={`absolute right-0 top-full mt-2 w-64 rounded-xl border shadow-2xl overflow-hidden ${
                                            executiveMode
                                                ? "bg-white border-slate-200"
                                                : "bg-[#0d1526] border-white/10"
                                        }`}
                                        role="menu"
                                        data-testid="nav-profile-menu"
                                    >
                                        {/* Header */}
                                        <div className={`px-4 py-3 border-b ${executiveMode ? "border-slate-100" : "border-white/5"}`}>
                                            <p className={`text-sm font-semibold truncate ${executiveMode ? "text-slate-900" : "text-text-primary"}`}>
                                                {user.name || "User"}
                                            </p>
                                            <p className={`text-xs truncate ${executiveMode ? "text-slate-500" : "text-text-secondary"}`}>
                                                {user.email}
                                            </p>
                                            {user.role === "admin" && (
                                                <span className="inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
                                                    ADMIN
                                                </span>
                                            )}
                                        </div>

                                        {/* Menu items */}
                                        <Link
                                            to="/genz-credit-scoring/history"
                                            onClick={() => setProfileOpen(false)}
                                            className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                                                executiveMode
                                                    ? "text-slate-700 hover:bg-slate-50"
                                                    : "text-text-primary hover:bg-white/5"
                                            }`}
                                            role="menuitem"
                                            data-testid="nav-profile-history"
                                        >
                                            <History size={15} className={executiveMode ? "text-slate-500" : "text-cyan-signal"} />
                                            My Score History
                                        </Link>
                                        <Link
                                            to="/genz-credit-scoring/flow"
                                            onClick={() => {
                                                setProfileOpen(false);
                                                // Bug 4: clear saved wizard progress so the wizard always
                                                // starts at step 1 when explicitly generating a new score
                                                try { sessionStorage.removeItem("wizard_progress"); } catch (_) {}
                                            }}
                                            className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                                                executiveMode
                                                    ? "text-slate-700 hover:bg-slate-50"
                                                    : "text-text-primary hover:bg-white/5"
                                            }`}
                                            role="menuitem"
                                            data-testid="nav-profile-newscore"
                                        >
                                            <User size={15} className={executiveMode ? "text-slate-500" : "text-blue-primary"} />
                                            Generate New Score
                                        </Link>
                                        {user.role === "admin" && (
                                            <Link
                                                to="/genz-credit-scoring/admin"
                                                onClick={() => setProfileOpen(false)}
                                                className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                                                    executiveMode
                                                        ? "text-slate-700 hover:bg-slate-50"
                                                        : "text-text-primary hover:bg-white/5"
                                                }`}
                                                role="menuitem"
                                                data-testid="nav-profile-admin"
                                            >
                                                <Shield size={15} className={executiveMode ? "text-slate-500" : "text-purple-soft"} />
                                                Admin Console
                                            </Link>
                                        )}
                                        <div className={`border-t my-1 ${executiveMode ? "border-slate-100" : "border-white/5"}`} />
                                        <button
                                            onClick={handleLogout}
                                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                                                executiveMode
                                                    ? "text-red-600 hover:bg-red-50"
                                                    : "text-red-starter hover:bg-red-starter/10"
                                            }`}
                                            role="menuitem"
                                            data-testid="nav-profile-logout"
                                        >
                                            <LogOut size={15} /> Sign out
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ) : (
                        <Link
                            to="/login"
                            className={`font-medium px-5 py-2 rounded-xl transition-all text-sm ${
                                executiveMode
                                    ? "bg-slate-900 text-white hover:bg-slate-700"
                                    : "bg-blue-primary text-white hover:bg-blue-primary/90 hover:shadow-[0_0_20px_rgba(99,102,241,0.4)]"
                            }`}
                            data-testid="nav-login-button"
                        >
                            Login
                        </Link>
                    )}
                </div>

                {/* Mobile hamburger */}
                <button
                    className={`lg:hidden p-2 ${executiveMode ? "text-slate-700" : "text-text-primary"}`}
                    onClick={() => setOpen(!open)}
                    data-testid="nav-mobile-toggle"
                    aria-label={open ? "Close menu" : "Open menu"}
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {/* Mobile menu */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: DURATION.pageTransition, ease: EASING.standard }}
                        className={`lg:hidden backdrop-blur-2xl border-b overflow-hidden ${
                            executiveMode
                                ? "bg-white/95 border-slate-200"
                                : "bg-[#030712]/95 border-white/10"
                        }`}
                    >
                        <div className="px-6 py-4 flex flex-col gap-3">
                            {/* User header on mobile (if logged in) */}
                            {user && (
                                <div className={`flex items-center gap-3 pb-3 mb-1 border-b ${executiveMode ? "border-slate-200" : "border-white/5"}`}>
                                    {user.avatar_url ? (
                                        <img src={user.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                                    ) : (
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${executiveMode ? "bg-slate-100 text-slate-700" : "bg-blue-primary/20 text-blue-primary"}`}>
                                            {(user.name || user.email || "U").charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <p className={`text-sm font-semibold truncate ${executiveMode ? "text-slate-900" : "text-text-primary"}`}>
                                            {user.name || "User"}
                                        </p>
                                        <p className={`text-xs truncate ${executiveMode ? "text-slate-500" : "text-text-secondary"}`}>
                                            {user.email}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {navLinks.map(link => (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    onClick={() => setOpen(false)}
                                    className={`text-sm font-medium py-2 transition-colors ${
                                        location.pathname === link.to
                                            ? executiveMode ? "text-slate-900" : "text-text-primary"
                                            : executiveMode ? "text-slate-600" : "text-text-secondary"
                                    }`}
                                >
                                    {link.label}
                                </Link>
                            ))}
                            {user && authedNavLinks.map(link => (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    onClick={() => setOpen(false)}
                                    className={`text-sm font-medium py-2 transition-colors ${
                                        location.pathname === link.to
                                            ? executiveMode ? "text-slate-900" : "text-text-primary"
                                            : executiveMode ? "text-slate-600" : "text-text-secondary"
                                    }`}
                                >
                                    {link.label}
                                </Link>
                            ))}
                            {user?.role === "admin" && (
                                <Link
                                    to="/genz-credit-scoring/admin"
                                    onClick={() => setOpen(false)}
                                    className={`text-sm font-medium py-2 ${executiveMode ? "text-purple-700" : "text-purple-soft"}`}
                                >
                                    Admin Console
                                </Link>
                            )}
                            <div className={`pt-3 mt-1 ${executiveMode ? "border-t border-slate-200" : "border-t border-white/5"}`}>
                                {user ? (
                                    <button
                                        onClick={() => { handleLogout(); setOpen(false); }}
                                        className={`flex items-center gap-2 text-sm ${executiveMode ? "text-red-600 hover:text-red-700" : "text-red-starter hover:text-red-starter/80"}`}
                                        data-testid="nav-mobile-logout"
                                    >
                                        <LogOut size={16} /> Sign out
                                    </button>
                                ) : (
                                    <Link
                                        to="/login"
                                        onClick={() => setOpen(false)}
                                        className={`flex items-center gap-2 text-sm font-medium ${executiveMode ? "text-slate-800" : "text-blue-primary"}`}
                                        data-testid="nav-mobile-login"
                                    >
                                        <User size={16} /> Login
                                    </Link>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
}
